import { Link } from "react-router-dom";
import Avatar from "@/components/Avatar";
import { useCanViewPersonContactBool } from "@/hooks/useCapabilities";
import { useFlags } from "@/hooks/useFeatureFlags";
import { getDefaultTab } from "@/lib/defaultTab";
import { m360Paths } from "@/lib/m360Paths";

export type InteractionPersonSummaryPerson = {
  firstName?: string | null;
  lastName?: string | null;
  profilePhoto?: string | null;
  pgaId?: string | null;
  employments?:
    | {
        isPrimary?: boolean | null;
        facility?: {
          name?: string | null;
        } | null;
      }[]
    | null;
  primarySectionAffiliation?: {
    section?: {
      name?: string | null;
    } | null;
  } | null;
};

export function getInteractionPersonSubtitle(person: InteractionPersonSummaryPerson): string {
  const facility = person.employments?.find((employment) => employment?.isPrimary)?.facility?.name?.trim();
  const section = person.primarySectionAffiliation?.section?.name?.trim();
  return [facility, section].filter(Boolean).join(" · ");
}

export function resolveInteractionPersonPath(
  pgaId: string,
  options: { navigationPreferencesOn: boolean; defaultPersonTab: string | null; canViewContact: boolean }
): string {
  // Contact-only by product intent: from an interaction the person link lands on
  // overview, unless the user starred contact as their person default tab. Other
  // starred tabs (notes, documents, timeline, ...) intentionally fall through to
  // overview rather than deep-linking from this surface.
  if (options.navigationPreferencesOn && options.defaultPersonTab === "contact" && options.canViewContact) {
    return m360Paths.personContact(pgaId);
  }
  return m360Paths.personOverview(pgaId);
}

type InteractionPersonSummaryProps = {
  person: InteractionPersonSummaryPerson;
  className?: string;
  testId?: string;
};

export default function InteractionPersonSummary({
  person,
  className,
  testId = "interaction-person-summary"
}: InteractionPersonSummaryProps) {
  const flags = useFlags();
  const canViewContact = useCanViewPersonContactBool();
  const navigationPreferencesOn = flags?.["backoffice-navigation-preferences"] === true;

  const fullName = [person.firstName, person.lastName].filter(Boolean).join(" ").trim() || "Unknown Person";
  const subtitle = getInteractionPersonSubtitle(person);
  const pgaId = person.pgaId?.trim();
  const isLinked = Boolean(pgaId);

  const content = (
    <>
      <Avatar picture={person.profilePhoto || undefined} size="medium" className="flex-shrink-0" />
      <span className="ml-3 min-w-0">
        <span
          className={`block truncate text-title-large-bold text-sys-brand-on-surface${
            isLinked ? " group-hover:underline group-focus-visible:underline underline-offset-2" : ""
          }`}
        >
          {fullName}
        </span>
        {subtitle ? (
          <span className="block truncate text-body-medium text-sys-brand-on-surface-variant">{subtitle}</span>
        ) : null}
      </span>
    </>
  );

  const layoutClassName = `${isLinked ? "group " : ""}flex items-center min-w-0 rounded-md ${className ?? ""}`.trim();

  if (!pgaId) {
    return (
      <div data-testid={testId} className={layoutClassName}>
        {content}
      </div>
    );
  }

  const to = resolveInteractionPersonPath(pgaId, {
    navigationPreferencesOn,
    defaultPersonTab: getDefaultTab("people/person"),
    canViewContact
  });

  return (
    <Link
      data-testid={testId}
      to={to}
      aria-label={`View person ${fullName}`}
      className={`${layoutClassName} focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-sys-brand-primary`}
    >
      {content}
    </Link>
  );
}
